import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { getSharedAuthFromCookie, setSharedAuthCookie } from './cookieAuth';

// API Configuration:
// - Development: Use empty baseURL so Vite proxy (vite.config.ts) forwards /api to MailBox.API (5220)
// - Production: Uses empty baseURL (IIS reverse proxy routes /api to API server)
// - VITE_API_BASE_URL can override (e.g. point to a different API URL)
const getApiBaseUrl = (): string => {
  const envUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
  
  // If env var is explicitly set and not empty, use it
  if (envUrl) {
    return envUrl;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // Stage static host routes directly to stage API.
    if (host === 'stmail.qixit.com') {
      return 'https://mailbox-api-stage-611e.azurewebsites.net';
    }
    // Production host is static-only; route directly to API host.
    if (host === 'mail.qixit.com') {
      return 'https://mailbox-api-prod-611e.azurewebsites.net';
    }
  }
  
  // Development and production: relative URLs - proxy (dev) or IIS (prod) routes /api to API
  return '';
};

const API_BASE_URL = getApiBaseUrl();

console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 import.meta.env.VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
console.log('🔧 import.meta.env.PROD:', import.meta.env.PROD);

class ApiService {
  private api: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log('✅ ApiService initialized with baseURL:', API_BASE_URL);

    // Load tokens from localStorage on initialization
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    if (this.accessToken) {
      console.log('🔑 Loaded access token from localStorage');
    }
    
    // If no token in localStorage, check shared auth cookie (SSO from Products Portal)
    if (!this.accessToken) {
      const sharedAuth = getSharedAuthFromCookie();
      if (sharedAuth) {
        console.log('🔑 Found shared auth cookie from SSO, syncing to localStorage');
        this.accessToken = sharedAuth.token;
        localStorage.setItem('accessToken', sharedAuth.token);
        // Note: We don't have refreshToken from SSO, but the access token should work
      }
    }

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't attempt refresh if we're on the login page - let the error propagate
          if (window.location.pathname.includes('/login')) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const newAccessToken = await this.refreshAccessToken();
            if (newAccessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  setTokens(accessToken: string, refreshToken: string, userId?: string, username?: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    // Also store in localStorage for use by socialService
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    
    // Note: Shared cookie is set by authSlice.ts after login, not here
    // This is because we need userId and username which are in the login response
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    // Also clear from localStorage (including user info for hasAdvancedMailbox persistence)
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Note: Shared cookie is cleared by authSlice.ts on logout
  }

  getAccessToken() {
    return this.accessToken;
  }
  
  getBaseUrl() {
    return API_BASE_URL || window.location.origin;
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (!this.refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
        refreshToken: this.refreshToken,
      });

      const { accessToken, refreshToken } = response.data;
      this.setTokens(accessToken, refreshToken);
      return accessToken;
    } catch (error) {
      return null;
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;

